// TourStep.tsx - ValuLearn branded onboarding tooltip
import React from 'react';
import { X, ArrowLeft, ArrowRight, Check, Sparkle } from '@phosphor-icons/react';
import { TourStepConfig } from '../../contexts/OnboardingContext';
import { TooltipPosition } from '../../hooks/useSpotlight';

interface TourStepProps {
  step: TourStepConfig;
  position: TooltipPosition;
  currentIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}

const TourStep: React.FC<TourStepProps> = ({
  step,
  position,
  currentIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isFirstStep,
  isLastStep,
}) => {
  // ValuLearn brand colors - exact from tailwind config
  const colors = {
    // Dark Blue - main background
    darkBlue: {
      darker: '#243747',
      DEFAULT: '#38556e',
      light: '#8ca2b4',
      veryLight: '#dbe3ea',
    },
    // Blue - primary accents
    blue: {
      darker: '#56798a',
      DEFAULT: '#79a5c0',
      light: '#bcd4e3',
      veryLight: '#e8f0f5',
    },
    // Orange - highlight accent
    orange: {
      darker: '#b67c2e',
      DEFAULT: '#e9a23b',
      light: '#f1c97f',
    },
    // Grey - subtle elements
    grey: {
      darker: '#2a3039',
      DEFAULT: '#394150',
      light: '#9da3ac',
    },
  };

  // Arrow styles based on placement
  // Orange arrow with dark blue border effect (two layered triangles)
  const getArrowStyles = () => {
    const arrowSize = 12;
    const borderSize = arrowSize + 1; // Slightly larger for thin border effect
    const arrowColor = colors.orange.DEFAULT; // #e9a23b
    const borderColor = '#314255'; // Custom dark blue border

    const baseStyles = {
      position: 'absolute' as const,
      width: 0,
      height: 0,
    };

    switch (position.placement) {
      case 'top':
        return {
          outer: {
            ...baseStyles,
            bottom: -borderSize,
            left: `${position.arrowPosition}%`,
            transform: 'translateX(-50%)',
            borderLeft: `${borderSize}px solid transparent`,
            borderRight: `${borderSize}px solid transparent`,
            borderTop: `${borderSize}px solid ${borderColor}`,
          },
          inner: {
            ...baseStyles,
            bottom: -arrowSize + 2,
            left: `${position.arrowPosition}%`,
            transform: 'translateX(-50%)',
            borderLeft: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid transparent`,
            borderTop: `${arrowSize}px solid ${arrowColor}`,
          },
        };
      case 'bottom':
        return {
          outer: {
            ...baseStyles,
            top: -borderSize,
            left: `${position.arrowPosition}%`,
            transform: 'translateX(-50%)',
            borderLeft: `${borderSize}px solid transparent`,
            borderRight: `${borderSize}px solid transparent`,
            borderBottom: `${borderSize}px solid ${borderColor}`,
          },
          inner: {
            ...baseStyles,
            top: -arrowSize + 2,
            left: `${position.arrowPosition}%`,
            transform: 'translateX(-50%)',
            borderLeft: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid ${arrowColor}`,
          },
        };
      case 'left':
        return {
          outer: {
            ...baseStyles,
            right: -borderSize,
            top: `${position.arrowPosition}%`,
            transform: 'translateY(-50%)',
            borderTop: `${borderSize}px solid transparent`,
            borderBottom: `${borderSize}px solid transparent`,
            borderLeft: `${borderSize}px solid ${borderColor}`,
          },
          inner: {
            ...baseStyles,
            right: -arrowSize + 2,
            top: `${position.arrowPosition}%`,
            transform: 'translateY(-50%)',
            borderTop: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid transparent`,
            borderLeft: `${arrowSize}px solid ${arrowColor}`,
          },
        };
      case 'right':
        return {
          outer: {
            ...baseStyles,
            left: -borderSize,
            top: `${position.arrowPosition}%`,
            transform: 'translateY(-50%)',
            borderTop: `${borderSize}px solid transparent`,
            borderBottom: `${borderSize}px solid transparent`,
            borderRight: `${borderSize}px solid ${borderColor}`,
          },
          inner: {
            ...baseStyles,
            left: -arrowSize + 2,
            top: `${position.arrowPosition}%`,
            transform: 'translateY(-50%)',
            borderTop: `${arrowSize}px solid transparent`,
            borderBottom: `${arrowSize}px solid transparent`,
            borderRight: `${arrowSize}px solid ${arrowColor}`,
          },
        };
      default:
        return { outer: baseStyles, inner: baseStyles };
    }
  };

  // Apply topAdjustment and leftAdjustment from step config
  const adjustedTop = position.top + (step.topAdjustment || 0);
  const adjustedLeft = position.left + (step.leftAdjustment || 0);
  const tooltipWidth = step.width || 320;

  return (
    <div
      className="fixed"
      style={{
        top: adjustedTop,
        left: adjustedLeft,
        width: tooltipWidth,
        zIndex: 10002,
        animation: 'tourStepEnter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Main tooltip card */}
      <div
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(145deg, ${colors.darkBlue.darker} 0%, ${colors.darkBlue.DEFAULT} 100%)`,
          borderRadius: '14px',
          boxShadow: `
            0 20px 40px -12px rgba(36, 55, 71, 0.6),
            0 0 0 1px rgba(121, 165, 192, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.08)
          `,
        }}
      >
        {/* Subtle top accent line */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '20px',
            right: '20px',
            height: '2px',
            background: `linear-gradient(90deg, transparent, ${colors.blue.DEFAULT}, transparent)`,
            opacity: 0.6,
          }}
        />

        {/* Background decorative element */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: '-30%',
            right: '-20%',
            width: '60%',
            height: '80%',
            background: `radial-gradient(ellipse, ${colors.blue.DEFAULT}15 0%, transparent 70%)`,
          }}
        />

        {/* Header */}
        <div
          className="relative flex items-center justify-between px-4 py-3"
          style={{ borderBottom: `1px solid ${colors.darkBlue.light}20` }}
        >
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center w-5 h-5 rounded"
              style={{
                background: `${colors.orange.DEFAULT}20`,
              }}
            >
              <Sparkle
                size={12}
                weight="fill"
                style={{ color: colors.orange.DEFAULT }}
              />
            </div>
            <span
              className="text-xs font-semibold tracking-wide"
              style={{ color: colors.blue.light }}
            >
              Step {currentIndex + 1} of {totalSteps}
            </span>
          </div>
          <button
            onClick={onSkip}
            className="p-1.5 rounded-md transition-all duration-200"
            style={{
              color: colors.darkBlue.light,
              background: 'transparent',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ffffff';
              e.currentTarget.style.background = `${colors.darkBlue.light}30`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = colors.darkBlue.light;
              e.currentTarget.style.background = 'transparent';
            }}
            aria-label="Close tour"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Content */}
        <div className="relative px-4 py-4">
          {/* Illustration (if provided) */}
          {step.illustration && (
            <div className="mb-3 flex justify-center">
              {step.illustration}
            </div>
          )}

          {/* Title */}
          <h3
            className="text-base font-bold mb-1.5"
            style={{ color: '#ffffff' }}
          >
            {step.title}
          </h3>

          {/* Description */}
          <p
            className="text-sm leading-relaxed"
            style={{ color: colors.blue.light }}
          >
            {step.description}
          </p>
        </div>

        {/* Footer with navigation */}
        <div
          className="relative flex items-center justify-between px-4 py-3"
          style={{
            borderTop: `1px solid ${colors.darkBlue.light}15`,
            background: `${colors.darkBlue.darker}80`,
          }}
        >
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {Array.from({ length: totalSteps }).map((_, index) => (
              <div
                key={index}
                className="transition-all duration-300"
                style={{
                  width: index === currentIndex ? '18px' : '6px',
                  height: '6px',
                  borderRadius: '3px',
                  background: index === currentIndex
                    ? colors.orange.DEFAULT
                    : index < currentIndex
                    ? colors.blue.DEFAULT
                    : `${colors.darkBlue.light}40`,
                  boxShadow: index === currentIndex
                    ? `0 0 8px ${colors.orange.DEFAULT}50`
                    : 'none',
                }}
              />
            ))}
          </div>

          {/* Navigation buttons */}
          <div className="flex items-center gap-2">
            {!isFirstStep && (
              <button
                onClick={onPrev}
                className="flex items-center gap-1 px-2.5 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
                style={{
                  color: colors.blue.light,
                  background: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ffffff';
                  e.currentTarget.style.background = `${colors.darkBlue.light}20`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.blue.light;
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <ArrowLeft size={14} weight="bold" />
                Back
              </button>
            )}
            <button
              onClick={onNext}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold rounded-md transition-all duration-200"
              style={{
                background: colors.blue.DEFAULT,
                color: colors.darkBlue.darker,
                boxShadow: `0 2px 8px ${colors.blue.DEFAULT}40`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = colors.blue.light;
                e.currentTarget.style.boxShadow = `0 4px 12px ${colors.blue.DEFAULT}50`;
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = colors.blue.DEFAULT;
                e.currentTarget.style.boxShadow = `0 2px 8px ${colors.blue.DEFAULT}40`;
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {isLastStep ? (
                <>
                  Done
                  <Check size={14} weight="bold" />
                </>
              ) : (
                <>
                  Next
                  <ArrowRight size={14} weight="bold" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Arrow - dual triangle for border effect (hidden if step.hideArrow) */}
      {!step.hideArrow && (() => {
        const arrowStyles = getArrowStyles();
        return (
          <>
            <div style={arrowStyles.outer} />
            <div style={arrowStyles.inner} />
          </>
        );
      })()}

      {/* Inline keyframes for entrance animation */}
      <style>{`
        @keyframes tourStepEnter {
          0% {
            opacity: 0;
            transform: scale(0.95) translateY(6px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default TourStep;
